<template>
  <div class="flex w-full justify-between space-y-2 bg-white">
    <div class="wrapper flex items-center">
      <avatar-with-name
        v-if="callActors.caller.first_name"
        :user="callActors.caller"
        data-cy="user-name"
      />
      <div class="flex items-center space-x-1">
        <icon-base
          data-cy="arrow-icon"
          :icon="IconArrow"
          :class="iconColor"
          width="12"
          height="12"
        />
        <p class="text-xs text-label" data-cy="call-status">
          {{ callStatus }}
        </p>
      </div>
      <avatar-with-name
        v-if="callActors.callee.first_name"
        :user="callActors.callee"
        data-cy="callee-name"
      />
      <div v-if="callLogData.to" class="flex items-center space-x-1">
        <span>&#8226;</span>
        <p class="text-xs text-label" data-cy="call-to">
          {{ callLogData.to }}
        </p>
      </div>
      <div v-if="log.data?.created_at" class="flex items-center space-x-1">
        <span>&#8226;</span>
        <date-time :date="log.data?.created_at" />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { CallSessionStatus } from "@/enums/communicationLogs";

import IconArrow from "@/components/icons/IconArrow.vue";
import AvatarWithName from "@/components/ui/AvatarWithName.vue";
import DateTime from "@/components/ui/ConvertibleDateTime.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type { CallLog } from "@/models/communicationLogs";

interface Props {
  log: CallLog;
  iconColor: string;
  currentColorOfStatus: number;
}

const { log, iconColor } = defineProps<Props>();

const { t } = useI18n();

const callActors = computed(() => {
  const { user, callee, is_outgoing } = log.data;
  const { first_name, last_name } = user ?? {};
  const { name } = callee || {};

  return {
    caller: is_outgoing ? { first_name, last_name } : { first_name: name },
    callee: is_outgoing ? { first_name: name } : { first_name, last_name },
  };
});

const callLogData = computed(() => log.data);

const statusLocaleMap = computed(() => {
  const map: Record<CallSessionStatus, string> = {
    [CallSessionStatus.Unreachable]: t("ACTIVITY.CONTACTS.UNREACHABLE"),
    [CallSessionStatus.InProgress]: t("ACTIVITY.CONTACTS.CALLED"),
    [CallSessionStatus.Answered]: t("ACTIVITY.CONTACTS.REACHED"),
    [CallSessionStatus.Ringing]: t("ACTIVITY.CONTACTS.CALLED"),
  };
  return map;
});

const callStatus = computed(() => {
  const status = log.data?.status;
  return statusLocaleMap.value[status];
});
</script>
